/**
 * UI formatting and color utility functions.
 *
 * Extracted from mock-data.ts — these are pure utilities with no
 * dependency on mock data and are used across production screens.
 */

export const formatCurrency = (amount: number): string => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount}`;
};

export const getScoreColor = (score: number): string => {
  if (score >= 80) return '#10B981'; // green
  if (score >= 60) return '#F59E0B'; // amber
  return '#EF4444'; // red
};

/** @deprecated Use getDimensionColor instead */
export const getCategoryColor = (category: string): string => {
  const colors: Record<string, string> = {
    team: '#8B5CF6',
    market: '#3B82F6',
    product: '#10B981',
    traction: '#F59E0B',
    financials: '#EC4899',
  };
  return colors[category] || '#6B7280';
};

/**
 * Get color for a 13-dimension scoring category
 * Uses a cohesive color palette for visualization
 */
export const getDimensionColor = (dimension: string): string => {
  const colors: Record<string, string> = {
    // Core dimensions (warm colors)
    team: '#8B5CF6',              // Purple - people
    product: '#10B981',           // Emerald - building
    market: '#3B82F6',            // Blue - opportunity
    traction: '#F59E0B',          // Amber - progress

    // Competitive dimensions (cool colors)
    defensibility: '#6366F1',     // Indigo - moat
    competition: '#0EA5E9',       // Sky - landscape

    // Business dimensions (pink/rose)
    'business-model': '#EC4899',  // Pink - economics
    'financial-health': '#F43F5E', // Rose - money

    // Strategic dimensions (teal/cyan)
    timing: '#14B8A6',            // Teal - now
    'vision-story': '#06B6D4',    // Cyan - narrative
    'gtm-execution': '#22D3EE',   // Light cyan - sales

    // Operational dimensions (lime/green)
    'execution-velocity': '#84CC16', // Lime - speed
    scalability: '#22C55E',       // Green - growth
  };
  return colors[dimension] || '#6B7280';
};

/**
 * Get risk badge color and label
 */
export const getRiskBadge = (riskLevel: string): { color: string; bgColor: string; label: string } => {
  const badges: Record<string, { color: string; bgColor: string; label: string }> = {
    none: { color: '#10B981', bgColor: '#D1FAE5', label: 'No Risk' },
    low: { color: '#3B82F6', bgColor: '#DBEAFE', label: 'Low Risk' },
    medium: { color: '#F59E0B', bgColor: '#FEF3C7', label: 'Medium Risk' },
    high: { color: '#EF4444', bgColor: '#FEE2E2', label: 'High Risk' },
    critical: { color: '#DC2626', bgColor: '#FECACA', label: 'Critical' },
  };
  return badges[riskLevel] || badges.medium;
};

/**
 * Get evidence level badge
 */
export const getEvidenceBadge = (evidenceLevel: string): { color: string; bgColor: string; label: string } => {
  const badges: Record<string, { color: string; bgColor: string; label: string }> = {
    verified: { color: '#10B981', bgColor: '#D1FAE5', label: 'Verified' },
    uploaded: { color: '#3B82F6', bgColor: '#DBEAFE', label: 'Uploaded' },
    'in-progress': { color: '#F59E0B', bgColor: '#FEF3C7', label: 'In Progress' },
    'self-declared': { color: '#8B5CF6', bgColor: '#EDE9FE', label: 'Self-Declared' },
    missing: { color: '#6B7280', bgColor: '#F3F4F6', label: 'Missing' },
  };
  return badges[evidenceLevel] || badges.missing;
};
